<script setup>
/**
 * The ticket-first head of the Details sidebar, per the tickets mockup: type
 * badge, title, state pill dropdown, the tracker's linked conversations, and a
 * timeline of what happened to the ticket. Assignee / team / priority and the
 * attribute key-values stay with the existing sidebar sections below - this
 * section only adds what a plain conversation does not have.
 *
 * Unlinking is bookkeeping only; it never touches the linked conversation's
 * own status (the reopen guard the backend enforces by never cascading).
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import ConversationApi from 'dashboard/api/inbox/conversation';

import StatusPill from 'dashboard/components-next/Conversation/StatusPill.vue';
import StatePillMenu from './StatePillMenu.vue';
import {
  TICKET_TYPE,
  TICKET_TYPE_META,
  deriveTicketState,
  ticketTypeOf,
} from 'dashboard/helper/conversationLens';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const { t } = useI18n();
const router = useRouter();

const links = ref([]);
const isLoadingLinks = ref(false);

const ticketType = computed(() => ticketTypeOf(props.conversation));
const typeMeta = computed(() => TICKET_TYPE_META[ticketType.value]);
const isTracker = computed(() => ticketType.value === TICKET_TYPE.TRACKER);

const title = computed(
  () =>
    props.conversation.subject ||
    props.conversation.additional_attributes?.mail_subject ||
    t('TICKETS.EMPTY.NO_SUBJECT')
);

// The state-change trail: the ticket's activity messages already record every
// state move, link and assignment, so they are the timeline. Newest first.
const timeline = computed(() =>
  (props.conversation.messages || [])
    .filter(message => message.message_type === MESSAGE_TYPE.ACTIVITY)
    .slice(-8)
    .reverse()
);

const fetchLinks = async () => {
  if (!isTracker.value) return;
  isLoadingLinks.value = true;
  try {
    const { data } = await ConversationApi.getTicketLinks(
      props.conversation.id
    );
    links.value = data.payload.linked_conversations;
  } catch (error) {
    useAlert(t('TICKETS.DETAIL.LINKED.FETCH_ERROR'));
  } finally {
    isLoadingLinks.value = false;
  }
};

const unlink = async link => {
  try {
    await ConversationApi.deleteTicketLink({
      ticketId: props.conversation.id,
      linkId: link.id,
    });
    links.value = links.value.filter(({ id }) => id !== link.id);
    useAlert(t('TICKETS.DETAIL.LINKED.UNLINK_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.DETAIL.LINKED.UNLINK_ERROR'));
  }
};

const openConversation = link =>
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: link.conversation.id },
  });

watch(() => props.conversation.id, fetchLinks);
onMounted(fetchLinks);
</script>

<template>
  <div class="flex flex-col gap-3 px-4 py-3 border-b border-n-weak">
    <div class="flex items-center gap-2">
      <span
        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
        :class="typeMeta.badgeClass"
      >
        <span :class="typeMeta.icon" class="size-3 shrink-0" />
        {{ t(typeMeta.labelKey) }}
      </span>
      <span class="text-xs font-medium text-n-slate-10">
        #{{ conversation.id }}
      </span>
    </div>

    <h3 class="text-sm font-semibold text-n-slate-12">{{ title }}</h3>

    <StatePillMenu :conversation="conversation" />

    <div v-if="isTracker" class="flex flex-col gap-1.5">
      <h4 class="text-xs font-medium tracking-wide uppercase text-n-slate-10">
        {{ t('TICKETS.DETAIL.LINKED.TITLE', { count: links.length }) }}
      </h4>
      <p v-if="isLoadingLinks" class="text-xs text-n-slate-10">
        {{ t('TICKETS.DETAIL.LINKED.LOADING') }}
      </p>
      <p v-else-if="!links.length" class="text-xs text-n-slate-10">
        {{ t('TICKETS.DETAIL.LINKED.EMPTY') }}
      </p>
      <div
        v-for="link in links"
        :key="link.id"
        class="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-n-alpha-1"
      >
        <button
          type="button"
          class="flex items-center flex-1 min-w-0 gap-2 text-start"
          :data-testid="`linked-conversation-${link.conversation.id}`"
          @click="openConversation(link)"
        >
          <span class="text-xs font-medium truncate text-n-slate-12">
            {{
              link.conversation.contact_name ||
              t('TICKETS.DETAIL.LINKED.UNKNOWN_CONTACT')
            }}
          </span>
          <span class="text-xs shrink-0 text-n-slate-10">
            #{{ link.conversation.id }}
          </span>
        </button>
        <StatusPill :state="deriveTicketState(link.conversation)" icon-only />
        <button
          type="button"
          :data-testid="`unlink-conversation-${link.conversation.id}`"
          :title="t('TICKETS.DETAIL.LINKED.UNLINK')"
          :aria-label="t('TICKETS.DETAIL.LINKED.UNLINK')"
          class="grid rounded size-5 shrink-0 place-items-center text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-ruby-11"
          @click="unlink(link)"
        >
          <span class="i-lucide-unlink size-3.5" />
        </button>
      </div>
    </div>

    <div v-if="timeline.length" class="flex flex-col gap-1.5">
      <h4 class="text-xs font-medium tracking-wide uppercase text-n-slate-10">
        {{ t('TICKETS.DETAIL.TIMELINE.TITLE') }}
      </h4>
      <div
        v-for="entry in timeline"
        :key="entry.id"
        class="flex items-start gap-2"
      >
        <span class="mt-1.5 rounded-full size-1.5 shrink-0 bg-n-slate-8" />
        <p class="flex-1 min-w-0 text-xs text-n-slate-11">
          {{ entry.content }}
        </p>
        <span class="text-[11px] shrink-0 text-n-slate-10">
          {{ dynamicTime(entry.created_at) }}
        </span>
      </div>
    </div>
  </div>
</template>
